import React, {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
  useRef,
  memo,
  useMemo,
  useCallback,
} from "react";
// import * as PIXI from "pixi.js"; // Not needed anymore

import { useDispatch, useSelector } from "react-redux";
import {
  selectFreeSpinsCurrentRound,
  selectFreeSpinsPlayed,
  selectFreeSpinsServerLevel,
  selectFreeSpinsTriggerScatterCell,
  selectFreeSpinsPackageTier,
  selectFreeSpinsAwarded,
  selectFreeSpinsEntrySequencePending,
  selectAutoPlaySpeed,
  selectBet,
  selectFreeRoundsActive,
  selectFreeRoundsBet,
} from "../../../State/selectors";
import { toStaticCell } from "../../Main/GambleScatter/gambleScatterConfig";
import {
  maskReelForMystery,
  resolveReelForMystery,
} from "../../Shared/mysteryStaging";
import { goldenShownBoard } from "../../Shared/goldenMysteryStaging";
import { goldenCellTexture } from "../../Main/Mystery/goldenMysteryConfig";
import {
  MYSTERY_SYMBOL_ID,
  SCATTER_2_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
} from "../../../SymbolData";
import FreeSpinBasicReelController from "./FreeSpinBasicReelController"; // New controller
import { convertedLowSymbolIds } from "../../../SymbolData";
import { safeInit } from "../../../utilities";
import { Settings } from "../../../settings";
import FreeSpinBasicDimReelOverlay from "../FreeSpinBasicDimReelOverlay/FreeSpinBasicDimReelOverlay";
import { useFreeSpinBasicLandingAnimations } from "../../../Hooks/useFreeSpinBasicLandingAnimations";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";

/**
 * Detects if the current round is a Hold & Win trigger (6+ coins).
 * Used to skip spin complete delay for trigger rounds.
 */
const detectHoldAndWinTrigger = (round) => {
  if (!round?.reels || !Array.isArray(round.reels)) return false;

  let coinCount = 0;

  for (let reelIndex = 0; reelIndex < round.reels.length; reelIndex++) {
    const reel = round.reels[reelIndex];
    if (!Array.isArray(reel)) continue;

    for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
      const symbolId = reel[symbolIndex];
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        coinCount++;
      }
    }
  }

  return coinCount >= 6;
};

const FreeSpinBasicReel = memo(
  forwardRef(
    (
      {
        reel,
        index,
        symbolWidth,
        symbolHeight,
        symbolsSpriteSheet,
        symbolsSpriteSheetBlurred,
        sharedSymbolsContainerRef,
        reelXOffset,
      },
      ref,
    ) => {
      const dispatch = useDispatch();
      const reelController = useRef(null);
      const dimOverlayRef = useRef(null);
      // THIS board's round, not the base game's. game.currentRound is
      // overwritten by every response — including our own free spins — so
      // reading it here would leave the two boards showing each other's
      // screens. See freeSpinsSlice.currentRound.
      const currentRound = useSelector(selectFreeSpinsCurrentRound);
      // ── The arrival board is a PHOTOGRAPH ──
      //
      // No free spin has been played, so the board is the base game's trigger
      // spin, and its mysteries were revealed down there. The room refuses to
      // reveal them again (useFreeSpinBasicWinController, the arrival-board
      // refusal) — so masking them back to the closed mystery here left every
      // one of them a closed mystery for the whole arrival. Golden cells hid it
      // under their composites; a REGULAR mystery showed it plainly. Vladimir,
      // 2026-09-24: *"we had textures of mystery symbols copied onto the
      // board... we must have already revealed things."*
      //
      // Keyed on exactly the rule that refuses the reveal — `played === 0` —
      // so the two cannot disagree: resolved whenever nothing will reveal it,
      // masked whenever something will. A completion reload's board carries
      // `isTriggerBoard` too, but it has played spins and DOES replay its
      // reveal, so it must stay masked.
      const freeSpinsPlayed = useSelector(selectFreeSpinsPlayed);
      const paintBoard = (reelData, round) =>
        freeSpinsPlayed === 0
          ? resolveReelForMystery(reelData, round, index)
          : maskReelForMystery(reelData, round, index);
      // ── The round, readable by a controller that does not exist yet ──
      //
      // The controller is built inside a requestAnimationFrame retry, so it is
      // null for at least one frame after mount. On a reload the round is
      // already in the store before that, so the data effect below fired once
      // against a null ref and never again — and the constructor seeds from
      // the `reel` prop, which FreeSpinBasicReelSet takes from the BASE round.
      // The player resumed looking at the trigger spin's screen.
      //
      // Same fix the overlay reel next door already uses for `bet`: write the
      // value unconditionally, and have the constructor read the ref.
      const roundRef = useRef(currentRound);
      roundRef.current = currentRound;

      // ── The level decides what may spin ──
      //
      // GDD §6: each level converts a low symbol into Mystery for the rest of
      // the round, and the backend swaps reel strip accordingly. The blur has
      // to follow or it shows symbols the strip no longer contains.
      //
      // serverLevel is 1-indexed, so the GDD level is one lower. NOT the room
      // index, which is `level - 2` clamped and therefore merges levels 0 and 1
      // — using it would leave l4 spinning for a whole conversion too long.
      // The won package and where it sits — see the settled-cell block in the
      // data effect below.
      // See where it is set — it exists so effects can depend on the controller
      // actually being there.
      const [controllerReady, setControllerReady] = useState(false);
      const triggerScatterCell = useSelector(selectFreeSpinsTriggerScatterCell);
      const packageTier = useSelector(selectFreeSpinsPackageTier);
      const awardedSpins = useSelector(selectFreeSpinsAwarded);
      const entrySequencePending = useSelector(
        selectFreeSpinsEntrySequencePending,
      );

      const serverLevel = useSelector(selectFreeSpinsServerLevel);
      const convertedIds = useMemo(
        () => convertedLowSymbolIds((serverLevel ?? 1) - 1),
        [serverLevel],
      );
      // Written unconditionally, so a level already in the store before this
      // component mounts still reaches the constructor below — the same reason
      // roundRef exists.
      const convertedRef = useRef(convertedIds);
      convertedRef.current = convertedIds;
      const autoPlaySpeed = useSelector(selectAutoPlaySpeed);

      // Use free rounds bet when active, otherwise use regular bet - memoized
      const freeRoundsActive = useSelector(selectFreeRoundsActive);
      const freeRoundsBet = useSelector(selectFreeRoundsBet);
      const regularBet = useSelector(selectBet);
      const bet = useMemo(
        () =>
          FEATURES_CONFIG.FREE_ROUNDS.ENABLED &&
          freeRoundsActive &&
          freeRoundsBet
            ? freeRoundsBet
            : regularBet,
        [freeRoundsActive, freeRoundsBet, regularBet],
      );

      // Landing animations hook
      const { triggerReelLandingAnimations } = useFreeSpinBasicLandingAnimations();

      // 🎮 Initialize controller once
      useEffect(() => {
        let rafId = null;

        const tryInit = () => {
          rafId = null;
          if (reelController.current) return;

          const sharedContainer = sharedSymbolsContainerRef?.current;
          if (!sharedContainer) {
            rafId = requestAnimationFrame(tryInit);
            return;
          }

          // ── The FIRST paint must already be MASKED ──
          //
          // The board the server sends is the board AFTER the reveal, so handing
          // the constructor the raw reels paints the revealed symbols — and on a
          // reload that frame then sits on screen through the best part of a
          // second of scene building (measured: a 495ms reconciler stall and a
          // 361ms ticker stall between this and the masking effect below). The
          // player watched the answer, and the mystery frames arrived afterwards
          // as the reveal staged its spines over them.
          //
          // Same rule as setConvertedSymbolIds two lines down, for the same
          // reason: the constructor is the only chance to be right on frame one.
          //
          // Only ever masked against ITS OWN reels. The `reel` prop is the
          // base-round fallback, and masking that with this round's coords would
          // put frames on cells the coords were never about.
          const ownReels = roundRef.current?.reels?.[index];
          const initialReels = ownReels
            ? paintBoard(ownReels, roundRef.current)
            : reel;

          safeInit(() => {
            reelController.current = new FreeSpinBasicReelController(
              dispatch,
              sharedContainer,
              symbolsSpriteSheet,
              symbolsSpriteSheetBlurred,
              initialReels,
              index, // reelIndex
              symbolWidth,
              symbolHeight,
              triggerReelLandingAnimations, // Callback for landing animations
              autoPlaySpeed, // Current autoplay speed
              bet, // Current bet value
              reelXOffset, // Column x, stamped on each sprite
            );
            // Before the first spin: a reload can land mid-round at level 4,
            // and the constructor is the only chance to be right on frame one.
            reelController.current.setConvertedSymbolIds?.(convertedRef.current);
            // Published as STATE as well as the ref. The controller lands here
            // after a retry loop and a safeInit — a frame or more past mount,
            // and invisible to React — so any effect that needs it and does
            // not depend on it runs once against null and never again. The
            // settled scatter's card texture is exactly such an effect: its
            // round is already in the store when the room opens.
            setControllerReady(true);
          });
        };

        tryInit();

        return () => {
          setControllerReady(false);
          if (rafId !== null) cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // 🚫 Converted lows — the pool narrows as the level climbs.
      useEffect(() => {
        reelController.current?.setConvertedSymbolIds?.(convertedIds);
      }, [convertedIds]);

      // ↔️ Column position — reelSpacing changes with the viewport, and the
      // offset now lives on the sprites rather than on a parent container.
      useEffect(() => {
        reelController.current?.setReelXOffset?.(reelXOffset);
      }, [reelXOffset]);

      // Update reels when currentRound changes (API data arrives)
      useEffect(() => {
        if (!reelController.current) return;

        // ── Stage the mystery cells ──
        //
        // Identical to the base game (see Games/Main/Reel/Reel.jsx): the board
        // the server sends is the board AFTER the reveal, so the pre-reveal
        // state is put back here for the player to watch land.
        //
        // DISPLAY ONLY. freeSpins.currentRound keeps the resolved board,
        // because useFreeSpinBasicWinController reads it to pick which spine
        // celebrates.
        //
        // ── A RESTORED round DOES reach this branch with a mystery in it ──
        //
        // This used to say the opposite: that hydrateFreeSpinsFromInit strips
        // `mystery_feature` so the reveal cannot replay on reload. That was true
        // once and is not any more — the slice now hands the restored round over
        // WHOLE and says so at length, and detectFreeSpinsState carries
        // `storage` on `lastRound` for exactly this. So the mask matters just as
        // much on reload as on a live spin, and a stale comment here is the
        // first thing that would stop anyone looking.
        const reelData = paintBoard(currentRound?.reels?.[index], currentRound);
        // ── AN ARRIVAL BOARD IS A CLEAN BOARD ──
        //
        // `isTriggerBoard` marks the base-game screen this bonus is opening on,
        // and it is the one moment nothing may be carried in. Every baked
        // texture from the LAST round is wiped first, so the cells are repainted
        // from this board's own symbols and nothing else.
        //
        // The overrides are what needed this. setSymbolTextureOnly paints a
        // finished picture — a settled Golden Mystery coin, an open gamble
        // window — directly onto the reel sprite, and it is built to SURVIVE
        // every ordinary repaint. Right inside a round, wrong between two: a
        // board of 10s, 20s and a 100 from the previous free-spins game was
        // still painted on the reels when the next one opened.
        //
        // Before updateReels, so the wipe cannot undo the new board.
        if (currentRound?.isTriggerBoard === true) {
          reelController.current.resetStaticOverrides?.();
        }

        reelController.current.updateReels(reelData);

        // Update win state and trigger state for last reel only
        if (index === Settings.REEL_COUNT - 1) {
          // Update win state for sound selection
          reelController.current.setHasWinThisSpin?.(currentRound?.win > 0);

          // Update Hold & Win trigger state to skip spin complete delay
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);
          reelController.current.setIsHoldAndWinTrigger?.(isHoldAndWinTrigger);
        }
        // ── The picture UNDER the live card ──
        //
        // Exactly what the base game leaves behind when a reveal settles: the
        // cell keeps the awarded package as a flat texture with its spin count
        // painted on, and the live spine sits over it (see the
        // setSymbolTextureOnly call in useWinController's reveal block).
        //
        // Without this the reel would still be holding symbol 12 — a CLOSED
        // gold scatter — under a card that is already open, which shows at the
        // edges and the moment anything hides the overlay.
        //
        // TEXTURE ONLY, and for the same reason it is texture-only there: the
        // cell's identity must stay the scatter, because the win controller
        // reads currentSymbolId to decide which spine celebrates.
        // ── The GOLDEN cells under the same rule ──
        //
        // The block below does this for the gamble scatter; golden mystery
        // cells need exactly the same treatment and had none, so the room
        // opened on a board where every golden frame was missing and the bare
        // `my` cover was showing in its place.
        //
        // A golden cell is never in `reels` — the reel holds MYSTERY_SYMBOL_ID
        // and the frame-plus-symbol is painted over it as one flat composite.
        // That is the same call, the same texture and the same cache the base
        // game uses (see the goldenShownBoard loop in useWinController), so
        // this is the picture the player was already looking at, carried over.
        //
        // ── LEVEL 1, never the room's multiplier ──
        //
        // The free-spins win controller passes `goldenLevel` here because its
        // boards are PLAYED in the room and the GDD multiplies their coins by
        // the rung. This board was played in the BASE game and paid at x1; the
        // room's ladder has nothing to do with it. Scaling it would print
        // inflated values on scenery whose money is already in the balance.
        //
        // Texture only, for the reason the scatter block gives below: the
        // cell's identity must stay the mystery symbol.
        //
        // No spines and no sequences — the reveals all happened in the base
        // game. This is a photograph of the finished board.
        if (currentRound?.isTriggerBoard === true) {
          for (const cell of goldenShownBoard(currentRound, 1).values()) {
            if (cell.reel !== index) continue;
            // The gamble scatter is ON this board and IS a golden cell, but the
            // block below owns it: it paints the package the player actually
            // won with its pips spent, which this cannot know. Skipped rather
            // than painted and overwritten, so the cell's identity is never
            // flipped to the mystery symbol even for an instant — the win
            // controller reads currentSymbolId to pick the celebrating spine.
            if (
              Array.isArray(triggerScatterCell) &&
              triggerScatterCell[0] === cell.reel &&
              triggerScatterCell[1] === cell.row
            ) {
              continue;
            }
            // ── A SCATTER on this board has ALREADY been revealed ──
            //
            // `goldenCellTexture` builds TWO pictures for a `+FS`: the closed
            // golden door, and the plain `+N` its window shows once opened.
            // `revealed` is the only thing that tells them apart, and
            // warmGoldenCells builds the CLOSED one at boot — so a cell asked
            // for without the flag gets a shut door.
            //
            // The live feature knows which windows opened and says so, from its
            // own `plusRevealed` set (see useFreeSpinBasicWinController). That
            // set is runtime state and does not survive a reload, so every `+FS`
            // on a restored board came back as a closed golden panel with
            // nothing in it. Vladimir, 2026-09-23, on the board after a capped
            // round — round 1473843, cell [2,5], the one cell in
            // mystery_feature.coords that is absent from the payout's positions.
            //
            // Here it needs no set: this block only runs for `isTriggerBoard`,
            // which is a PHOTOGRAPH of a finished feature. Every scatter on it
            // has been through its reveal by definition — there is no other way
            // for the board to have ended.
            const revealedCell =
              cell.symbolId === SCATTER_3_SYMBOL_ID ||
              cell.symbolId === SCATTER_2_SYMBOL_ID
                ? { ...cell, revealed: true }
                : cell;
            const texture = goldenCellTexture(revealedCell);
            if (!texture) continue;
            reelController.current.setSymbolTextureOnly?.(
              cell.row,
              MYSTERY_SYMBOL_ID,
              texture,
            );
          }
        }

        if (
          currentRound?.isTriggerBoard === true &&
          Array.isArray(triggerScatterCell) &&
          triggerScatterCell[0] === index &&
          typeof packageTier === "number"
        ) {
          // ── ALWAYS SPENT: the live card owns the pips in this room ──
          //
          // This used to be `entrySequencePending ? null : 0` — lit during the
          // flourish, spent after it. That is one source of pips too many.
          //
          // The flourish fires each pip out of the LIVE card and calls
          // clearPip, which empties that pip's track. The `on` slots carry no
          // setup attachment, so an emptied track leaves a TRANSPARENT hole —
          // and a flat texture painted with the pip still lit shows straight
          // through it. Every pip was going out correctly on the card and not
          // one of them could be seen; they all appeared to vanish at the end
          // instead, which is simply this texture repainting spent.
          //
          // So the card is the only thing that draws pips while it is up, and
          // it starts with all of them lit — _playPips parks each on its own
          // track on the frame it is built. The texture underneath is the
          // picture the next spin carries away, and that picture is empty.
          //
          // The one frame this could show through is between the reel painting
          // and the card being built, and that is under the wipe.
          const cell = toStaticCell(
            { tier: packageTier, value: awardedSpins },
            { pips: 0 },
          );
          if (cell) {
            reelController.current.setSymbolTextureOnly?.(
              triggerScatterCell[1],
              cell.symbolId,
              cell.texture,
            );

          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        currentRound,
        controllerReady,
        triggerScatterCell,
        packageTier,
        awardedSpins,
        // Re-runs when the flourish ends, which is when the card becomes spent.
        entrySequencePending,
        // The arrival paints resolved, a played board masked — see paintBoard.
        freeSpinsPlayed,
      ]);

      // 📏 Resize handler
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.resizeSymbol(symbolWidth, symbolHeight);
      }, [symbolWidth, symbolHeight]);

      // ⚡ Auto play speed update
      useEffect(() => {
        if (
          reelController.current &&
          reelController.current.autoPlaySpeed !== autoPlaySpeed
        ) {
          reelController.current.autoPlaySpeed = autoPlaySpeed;
        }
      }, [autoPlaySpeed]);

      // 💰 Bet update - update coin values when bet changes
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.updateBet(bet);
      }, [bet]);

      // 🧹 Cleanup
      useEffect(() => {
        return () => {
          if (!reelController.current) return;

          // Clear all animations, timeouts, and PIXI Ticker callbacks
          reelController.current.clearAllTimeouts();

          // Destroy all spinning sprites and their PIXI objects (textures, containers)
          reelController.current._cleanupSpinningSprites();

          // Reset controller state on unmount
          reelController.current.isStopping = false;
          reelController.current.hasStopped = true;
          reelController.current._spinElapsed = 0;
        };
      }, []);

      // Memoize controller methods
      const startSpin = useCallback(() => {
        reelController.current?.spin();
      }, []);

      const startSpinNoWindUp = useCallback(() => {
        reelController.current?.spinNoWindUp();
      }, []);

      const stopSpin = useCallback(() => {
        reelController.current?.stopSpin();
      }, []);

      const startTurboSpin = useCallback(() => {
        reelController.current?.spinTurbo();
      }, []);

      const stopTurboSpin = useCallback(() => {
        reelController.current?.stopSpinTurbo();
      }, []);

      const startTensionSpin = useCallback(() => {
        reelController.current?.spinTension();
      }, []);

      const stopTensionSpin = useCallback(() => {
        reelController.current?.stopSpinTension();
      }, []);

      const freezeSpin = useCallback(() => {
        reelController.current?.freezeSpin();
      }, []);

      const restoreToIdleState = useCallback((symbolIds, betValue = null) => {
        reelController.current?.restoreToIdleState(symbolIds, betValue);
      }, []);

      const setOnReelLanded = useCallback((callback) => {
        if (reelController.current) {
          reelController.current.onReelLanded = callback;
        }
      }, []);

      const setOnLandingPeak = useCallback((callback) => {
        if (reelController.current) {
          reelController.current.onLandingPeak = callback;
        }
      }, []);

      const getReelController = useCallback(() => {
        return reelController.current;
      }, []);

      const getReelSpinComplete = useCallback(() => {
        return reelController.current?.hasStopped ?? false;
      }, []);

      const replaceVisibleSymbols = useCallback(
        (targetSymbolId, hideTextures = true) => {
          reelController.current?.replaceVisibleSymbols(
            targetSymbolId,
            hideTextures,
          );
        },
        [],
      );

      const replaceSymbolAtPosition = useCallback(
        (symbolIndex, targetSymbolId) => {
          reelController.current?.replaceSymbolAtPosition(
            symbolIndex,
            targetSymbolId,
          );
        },
        [],
      );

      const dimReel = useCallback(() => {
        dimOverlayRef.current?.dimReel();
      }, []);

      const undimReel = useCallback(() => {
        dimOverlayRef.current?.undimReel();
      }, []);

      // 🎛️ Expose imperative API for external reel control
      useImperativeHandle(
        ref,
        () => ({
          startSpin,
          startSpinNoWindUp,
          stopSpin,

          // Turbo mode (instant drop, no wind-up)
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,

          // Freeze spin instantly (for error popups)
          freezeSpin,

          // Restore reel to idle state with specific symbols (for error recovery)
          restoreToIdleState,

          // Set callback for when reel has fully landed (bounce complete)
          setOnReelLanded,

          // Set callback for overshoot peak (lowest visual point, before bounce-back)
          setOnLandingPeak,

          getReelController,
          getReelSpinComplete,

          // Expanding wild support
          replaceVisibleSymbols,
          replaceSymbolAtPosition,

          // Dim control methods for tension mode and other effects
          dimReel,
          undimReel,
        }),
        [
          startSpin,
          startSpinNoWindUp,
          stopSpin,
          startTurboSpin,
          stopTurboSpin,
          startTensionSpin,
          stopTensionSpin,
          freezeSpin,
          restoreToIdleState,
          setOnReelLanded,
          setOnLandingPeak,
          getReelController,
          getReelSpinComplete,
          replaceVisibleSymbols,
          replaceSymbolAtPosition,
          dimReel,
          undimReel,
        ],
      );

      // Sprites are parented into FreeSpinBasicReelSet's single shared container so all 5
      // reels' symbols are siblings and can sort by z-index. Nothing is
      // rendered here but the tension dim overlay.
      return (
        <>
          {FEATURES_CONFIG.TENSION.ENABLED && (
            <FreeSpinBasicDimReelOverlay
              ref={dimOverlayRef}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
            />
          )}
        </>
      );
    },
  ),
  (prev, next) =>
    prev.symbolWidth === next.symbolWidth &&
    prev.symbolHeight === next.symbolHeight &&
    prev.index === next.index &&
    // Sprites carry their own column x now, so a reelSpacing change must
    // re-render to re-run the setReelXOffset effect. reelSpacing can move
    // without symbolWidth (it also depends on dividerDimensions), which would
    // otherwise leave the columns misaligned from the dividers and overlay.
    prev.reelXOffset === next.reelXOffset,
  // Note: currentRound changes are handled by useEffect, not memo
);

export default FreeSpinBasicReel;
